import React, { useState, useEffect, useCallback, memo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getNotifications, markAllNotificationsRead, submitBugReport } from '../services/api';
import { Button } from '../components/ui/button';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from '../components/ui/dropdown-menu';
// Popover removed - using DropdownMenu for Contact
import { Sheet, SheetContent, SheetTrigger, SheetClose } from '../components/ui/sheet';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
  BookOpen, 
  User, 
  Bell, 
  Menu, 
  LogOut, 
  Plus, 
  Settings, 
  LayoutDashboard,
  X,
  Info,
  Home,
  Search,
  Tag,
  ShoppingBag,
  Shield,
  HelpCircle,
  Mail,
  Instagram,
  ChevronDown,
  Bug,
  Send,
  Loader2
} from 'lucide-react';

const PLATFORM_EMAIL = 'studentbooks.marketplace@hotmail.com';

export const Header = memo(() => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [pendingAvailabilityCount, setPendingAvailabilityCount] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  
  // Bug report state
  const [bugReportOpen, setBugReportOpen] = useState(false);
  const [bugTitle, setBugTitle] = useState('');
  const [bugDescription, setBugDescription] = useState('');
  const [submittingBug, setSubmittingBug] = useState(false);

  // Memoized fetch function - only runs when user changes
  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    try {
      const data = await getNotifications();
      setNotifications(data);
      setUnreadCount(data.filter(n => !n.read).length);
      // Count pending availability checks (unread ones with type availability_check)
      setPendingAvailabilityCount(data.filter(n => !n.read && n.type === 'availability_check').length);
    } catch (error) {
      // Silent fail for notifications - not critical
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchNotifications();
      // Reduce polling frequency to 60 seconds for better performance
      const interval = setInterval(fetchNotifications, 60000);
      return () => clearInterval(interval);
    }
  }, [user, fetchNotifications]);

  const handleMarkAllRead = useCallback(async () => {
    try {
      await markAllNotificationsRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark notifications as read:', error);
    }
  }, []);

  const handleLogout = useCallback(() => {
    logout();
    setMobileMenuOpen(false);
    navigate('/');
  }, [logout, navigate]);

  const handleSubmitBugReport = useCallback(async () => {
    if (!bugTitle.trim() || !bugDescription.trim()) {
      toast.error('Please fill in both title and description');
      return;
    }
    
    setSubmittingBug(true);
    try {
      await submitBugReport({
        title: bugTitle.trim(),
        description: bugDescription.trim(),
        user_email: user?.email || 'anonymous',
        user_name: user?.name || 'Anonymous User',
        page_url: window.location.href
      });
      toast.success('Bug report submitted! Thank you for your feedback.');
      setBugReportOpen(false);
      setBugTitle('');
      setBugDescription('');
    } catch (error) {
      toast.error('Failed to submit bug report. Please try again.');
    } finally {
      setSubmittingBug(false);
    }
  }, [bugTitle, bugDescription, user]);

  const isActive = useCallback((path) => location.pathname === path, [location.pathname]);

  const desktopNavLinks = [
    { to: '/info', label: 'How It Works', icon: Info },
    { to: '/seller-guide', label: 'Seller Guide', icon: HelpCircle },
    { to: '/policy', label: 'Policies', icon: Shield },
    { to: '/marketplace', label: 'Browse Books', icon: Search },
    ...(user ? [
      { to: '/sell', label: 'Sell', icon: Plus },
      { to: '/profile', label: 'Profile', icon: User },
    ] : []),
    ...(user?.is_admin ? [
      { to: '/admin', label: 'Admin', icon: LayoutDashboard },
    ] : []),
  ];

  const mobileNavLinks = [
    { to: '/', label: 'Home', icon: Home },
    { to: '/marketplace', label: 'Browse Books', icon: Search },
    { to: '/info', label: 'How It Works', icon: Info },
    { to: '/seller-guide', label: 'Seller Guide', icon: HelpCircle },
    { to: '/policy', label: 'Policies', icon: Shield },
    ...(user ? [
      { to: '/sell', label: 'Sell a Book', icon: Tag },
      { to: '/profile', label: 'My Profile', icon: User },
    ] : []),
    ...(user?.is_admin ? [
      { to: '/admin', label: 'Admin Dashboard', icon: LayoutDashboard },
    ] : []),
  ];

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200/50 shadow-sm" data-testid="main-header">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-8">
        <div className="flex items-center justify-between h-14 sm:h-16">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
            data-testid="logo-link"
          >
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-[#003366] rounded-xl flex items-center justify-center">
              <BookOpen className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
            </div>
            <span className="font-semibold text-[#003366] text-sm sm:text-base">
              Student<span className="text-[#F59E0B]">Books</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1">
            {desktopNavLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                data-testid={`nav-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                  isActive(link.to)
                    ? 'bg-[#003366] text-white'
                    : 'text-slate-600 hover:text-[#003366] hover:bg-slate-100'
                }`}
              >
                {link.label}
                {link.to === '/profile' && pendingAvailabilityCount > 0 && (
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                    isActive(link.to) ? 'bg-orange-400' : 'bg-orange-500 text-white'
                  }`}>
                    {pendingAvailabilityCount}
                  </span>
                )}
              </Link>
            ))}
            
            {/* Contact & Support Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  className="px-3 py-2 rounded-full text-sm font-medium text-slate-600 hover:text-[#003366] hover:bg-slate-100 flex items-center gap-1"
                  data-testid="contact-dropdown-btn"
                >
                  <Mail className="w-4 h-4" />
                  Contact
                  <ChevronDown className="w-3 h-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64 p-2">
                <DropdownMenuItem asChild className="p-0">
                  <a 
                    href={`mailto:${PLATFORM_EMAIL}`}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer w-full"
                  >
                    <Mail className="w-4 h-4 text-slate-600" />
                    <div>
                      <p className="font-medium text-sm">Email Us</p>
                      <p className="text-xs text-slate-500">{PLATFORM_EMAIL}</p>
                    </div>
                  </a>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="p-0">
                  <a 
                    href="https://instagram.com/mystudentbooks" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer w-full"
                  >
                    <Instagram className="w-4 h-4 text-slate-600" />
                    <div>
                      <p className="font-medium text-sm">Instagram</p>
                      <p className="text-xs text-slate-500">@mystudentbooks</p>
                    </div>
                  </a>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onSelect={() => setBugReportOpen(true)}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-red-50 transition-colors cursor-pointer text-red-600 focus:text-red-600 focus:bg-red-50"
                >
                  <Bug className="w-4 h-4" />
                  <div>
                    <p className="font-medium text-sm">Report a Bug</p>
                    <p className="text-xs text-red-400">Help us improve</p>
                  </div>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>

          {/* Right Side Actions */}
          <div className="flex items-center gap-1 sm:gap-2">
            {user ? (
              <>
                {/* Notifications */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="relative rounded-full w-9 h-9 sm:w-10 sm:h-10"
                      data-testid="notifications-btn"
                    >
                      <Bell className="w-4 h-4 sm:w-5 sm:h-5" />
                      {unreadCount > 0 && (
                        <span className="absolute -top-0.5 -right-0.5 sm:-top-1 sm:-right-1 w-4 h-4 sm:w-5 sm:h-5 bg-[#F59E0B] text-white text-xs rounded-full flex items-center justify-center">
                          {unreadCount > 9 ? '9+' : unreadCount}
                        </span>
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-72 sm:w-80">
                    <div className="flex items-center justify-between px-3 py-2 border-b">
                      <span className="font-semibold text-sm">Notifications</span>
                      {unreadCount > 0 && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={handleMarkAllRead}
                          className="text-xs h-auto py-1"
                        >
                          Mark all read
                        </Button>
                      )}
                    </div>
                    <ScrollArea className="h-64">
                      {notifications.length === 0 ? (
                        <div className="px-3 py-8 text-center text-slate-500 text-sm">
                          No notifications yet
                        </div>
                      ) : (
                        notifications.slice(0, 10).map((notification) => (
                          <div
                            key={notification.id}
                            className={`px-3 py-3 border-b last:border-0 ${
                              !notification.read ? 'bg-blue-50' : ''
                            }`}
                          >
                            <p className="text-sm">{notification.message}</p>
                            <p className="text-xs text-slate-500 mt-1">
                              {new Date(notification.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        ))
                      )}
                    </ScrollArea>
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* User Menu - Desktop */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="ghost" 
                      className="hidden sm:flex rounded-full gap-2 px-3"
                      data-testid="user-menu-btn"
                    >
                      <div className="w-8 h-8 bg-[#003366] rounded-full flex items-center justify-center">
                        <span className="text-white text-sm font-medium">
                          {user.name?.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <span className="hidden lg:block text-sm font-medium max-w-[100px] truncate">
                        {user.name}
                      </span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <div className="px-3 py-2 border-b">
                      <p className="font-medium">{user.name}</p>
                      <p className="text-xs text-slate-500 truncate">{user.email}</p>
                      <p className="text-sm text-[#003366] font-semibold mt-1">
                        Balance: ${user.balance?.toFixed(2) || '0.00'}
                      </p>
                    </div>
                    <DropdownMenuItem asChild>
                      <Link to="/profile" className="cursor-pointer flex items-center justify-between">
                        <div className="flex items-center">
                          <User className="w-4 h-4 mr-2" />
                          My Profile
                        </div>
                        {pendingAvailabilityCount > 0 && (
                          <span className="bg-orange-500 text-white text-xs px-2 py-0.5 rounded-full">
                            {pendingAvailabilityCount} check{pendingAvailabilityCount > 1 ? 's' : ''}
                          </span>
                        )}
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/sell" className="cursor-pointer">
                        <Plus className="w-4 h-4 mr-2" />
                        Sell a Book
                      </Link>
                    </DropdownMenuItem>
                    {user.is_admin && (
                      <DropdownMenuItem asChild>
                        <Link to="/admin" className="cursor-pointer">
                          <Settings className="w-4 h-4 mr-2" />
                          Admin Dashboard
                        </Link>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-red-600">
                      <LogOut className="w-4 h-4 mr-2" />
                      Sign Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <div className="hidden sm:flex items-center gap-2">
                <Link to="/login">
                  <Button 
                    variant="ghost" 
                    className="rounded-full text-sm"
                    data-testid="login-btn"
                  >
                    Sign In
                  </Button>
                </Link>
                <Link to="/register">
                  <Button 
                    className="bg-[#003366] hover:bg-[#002244] rounded-full text-sm"
                    data-testid="register-btn"
                  >
                    Get Started
                  </Button>
                </Link>
              </div>
            )}

            {/* Mobile Menu Button */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="md:hidden rounded-full w-9 h-9"
                  data-testid="mobile-menu-btn"
                >
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[280px] sm:w-[320px] p-0" hideCloseButton={true}>
                {/* Mobile Menu Header */}
                <div className="p-4 border-b bg-slate-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-[#003366] rounded-xl flex items-center justify-center">
                        <BookOpen className="w-4 h-4 text-white" />
                      </div>
                      <span className="font-semibold text-[#003366]">
                        Student<span className="text-[#F59E0B]">Books</span>
                      </span>
                    </div>
                    <SheetClose asChild>
                      <Button variant="ghost" size="icon" className="rounded-full w-8 h-8">
                        <X className="w-4 h-4" />
                      </Button>
                    </SheetClose>
                  </div>
                  
                  {/* User Info (if logged in) */}
                  {user && (
                    <div className="mt-4 flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#003366] rounded-full flex items-center justify-center">
                        <span className="text-white font-medium">
                          {user.name?.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{user.name}</p>
                        <p className="text-xs text-slate-500 truncate">{user.email}</p>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Navigation Links */}
                <div className="flex flex-col p-3">
                  {mobileNavLinks.map((link) => (
                    <Link
                      key={link.to}
                      to={link.to}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`flex items-center justify-between px-4 py-3 rounded-xl transition-all ${
                        isActive(link.to)
                          ? 'bg-[#003366] text-white'
                          : 'text-slate-700 hover:bg-slate-100'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <link.icon className="w-5 h-5" />
                        <span className="font-medium">{link.label}</span>
                      </div>
                      {link.to === '/profile' && pendingAvailabilityCount > 0 && (
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          isActive(link.to) ? 'bg-orange-400 text-white' : 'bg-orange-500 text-white'
                        }`}>
                          {pendingAvailabilityCount}
                        </span>
                      )}
                    </Link>
                  ))}
                  
                  {/* Auth Links for non-logged in users */}
                  {!user && (
                    <div className="mt-4 pt-4 border-t space-y-2">
                      <Link
                        to="/login"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-[#003366] text-[#003366] font-medium"
                      >
                        <User className="w-5 h-5" />
                        Sign In
                      </Link>
                      <Link
                        to="/register"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-[#003366] text-white font-medium"
                      >
                        <Plus className="w-5 h-5" />
                        Get Started
                      </Link>
                    </div>
                  )}
                  
                  {/* Logout for logged in users */}
                  {user && (
                    <div className="mt-4 pt-4 border-t">
                      <button
                        onClick={handleLogout}
                        className="flex items-center gap-3 px-4 py-3 rounded-xl text-red-600 hover:bg-red-50 w-full"
                      >
                        <LogOut className="w-5 h-5" />
                        <span className="font-medium">Sign Out</span>
                      </button>
                    </div>
                  )}
                  
                  {/* Contact & Report Bug - Always visible */}
                  <div className="mt-4 pt-4 border-t space-y-2">
                    <button
                      onClick={() => {
                        setMobileMenuOpen(false);
                        setBugReportOpen(true);
                      }}
                      className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-700 hover:bg-red-50 hover:text-red-600 w-full"
                    >
                      <Bug className="w-5 h-5" />
                      <span className="font-medium">Report a Bug</span>
                    </button>
                    <a
                      href={`mailto:${PLATFORM_EMAIL}`}
                      className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-700 hover:bg-slate-100 w-full"
                    >
                      <Mail className="w-5 h-5" />
                      <span className="font-medium">Email Us</span>
                    </a>
                    <a
                      href="https://instagram.com/mystudentbooks"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-700 hover:bg-slate-100 w-full"
                    >
                      <Instagram className="w-5 h-5" />
                      <span className="font-medium">@mystudentbooks</span>
                    </a>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
      
      {/* Bug Report Dialog */}
      <Dialog open={bugReportOpen} onOpenChange={setBugReportOpen}>
        <DialogContent className="sm:max-w-md mx-4">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bug className="w-5 h-5 text-red-500" />
              Report a Bug
            </DialogTitle>
            <DialogDescription>
              Found something not working? Let us know and we'll fix it ASAP!
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Issue Title</Label>
              <Input
                placeholder="e.g., Chat not loading"
                value={bugTitle}
                onChange={(e) => setBugTitle(e.target.value)}
                className="rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                placeholder="Please describe what happened, what you expected, and any steps to reproduce the issue..."
                value={bugDescription}
                onChange={(e) => setBugDescription(e.target.value)}
                className="rounded-xl min-h-[120px]"
              />
            </div>
            <p className="text-xs text-slate-500">
              Your report will be sent directly to our admin team for review.
            </p>
          </div>
          
          <DialogFooter className="flex flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              className="w-full sm:flex-1 rounded-xl"
              onClick={() => setBugReportOpen(false)}
              disabled={submittingBug}
            >
              Cancel
            </Button>
            <Button
              className="w-full sm:flex-1 bg-[#003366] hover:bg-[#002244] rounded-xl"
              onClick={handleSubmitBugReport}
              disabled={submittingBug}
            >
              {submittingBug ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Submit Report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </header>
  );
});

// Set display name for debugging
Header.displayName = 'Header';

export default Header;
